import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { pageMeta } from "@/lib/seo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RoavaCard, StatusChip, PrimaryButton, SecondaryButton, DangerButton } from "@/components/roava";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getMyTrip, getTripMembers, updateTripMemberColor } from "@/lib/trip.functions";
import { createInviteCodes, listInviteCodes, revokeInviteCode } from "@/lib/invites.functions";
import { getAdminOverview, getTripDiagnostics } from "@/lib/admin.functions";
import { colorForUser } from "@/lib/traveler-colors";
import { PageHeader } from "@/components/admin/PageHeader";
import { TravelerColorChip, TravelerColorPicker } from "@/components/admin/TravelerColorPicker";
import {
  evaluateReadiness,
  readinessChipVariant,
  summarizeReadiness,
  type ReadinessResult,
} from "@/lib/admin/traveler-readiness";

export const Route = createFileRoute("/_authenticated/admin/invites")({
  head: () => pageMeta({ title: 'Trip Invites — Roava by WGP Apps', description: 'Create and manage invite codes so friends can join your shared trip.', path: '/admin/invites' }),
  component: Invites,
});

function Invites() {
  const qc = useQueryClient();
  const tripFn = useServerFn(getMyTrip);
  const listFn = useServerFn(listInviteCodes);
  const createFn = useServerFn(createInviteCodes);
  const revokeFn = useServerFn(revokeInviteCode);
  const membersFn = useServerFn(getTripMembers);
  const setColorFn = useServerFn(updateTripMemberColor);
  const diagFn = useServerFn(getTripDiagnostics);
  const overviewFn = useServerFn(getAdminOverview);

  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["invites", tripId],
    queryFn: () => listFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const { data: membersData } = useQuery({
    queryKey: ["trip-members", tripId],
    queryFn: () => membersFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  // Cheap shared queries for readiness — diagnostics + overview (ping presence).
  // No per-member RPC fan-out from the list view.
  const { data: diagData } = useQuery({
    queryKey: ["trip-diagnostics", tripId],
    queryFn: () => diagFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 30_000,
  });
  const { data: overviewData } = useQuery({
    queryKey: ["admin-overview", tripId],
    queryFn: () => overviewFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 30_000,
  });

  // userId → readiness result, computed from already-loaded data.
  const readinessByUser = useMemo(() => {
    const out = new Map<string, ReadinessResult>();
    const members = membersData?.members ?? [];
    const diagMap = diagData?.byUser ?? {};
    const userPingCount = new Map<string, number>();
    for (const s of overviewData?.stats ?? []) {
      userPingCount.set(s.userId, s.pings ?? 0);
    }
    for (const m of members) {
      const result = evaluateReadiness({
        member: { joinedAt: m.joinedAt, submittedAt: m.submittedAt ?? null },
        diagnostics: diagMap[m.userId] ?? null,
        hasPings: (userPingCount.get(m.userId) ?? 0) > 0,
      });
      out.set(m.userId, result);
    }
    return out;
  }, [membersData?.members, diagData?.byUser, overviewData?.stats]);

  const readinessSummary = useMemo(
    () => summarizeReadiness(Array.from(readinessByUser.values())),
    [readinessByUser],
  );

  // Trip-wide totals from overview for the ops summary card.
  const opsTotals = useMemo(() => {
    const stats = overviewData?.stats ?? [];
    let queued = 0;
    let failed = 0;
    let recentTracking = 0;
    const now = Date.now();
    for (const s of stats) {
      queued += s.queued ?? 0;
      failed += s.failed ?? 0;
    }
    // Recent tracking = diag with last_native_location_at within 15m, OR diag
    // updated within 15m AND has any pings.
    for (const [uid, r] of readinessByUser) {
      const d = diagData?.byUser?.[uid];
      const lastLoc = d?.lastNativeLocationAt;
      if (lastLoc && now - new Date(lastLoc).getTime() < 15 * 60 * 1000) {
        recentTracking++;
        continue;
      }
      if (
        d?.updatedAt &&
        now - new Date(d.updatedAt).getTime() < 15 * 60 * 1000 &&
        r.severity === "ready"
      ) {
        recentTracking++;
      }
    }
    return { queued, failed, recentTracking };
  }, [overviewData?.stats, readinessByUser, diagData?.byUser]);

  const colorMut = useMutation({
    mutationFn: (vars: { userId: string; color: string | null }) =>
      setColorFn({ data: { tripId: tripId!, userId: vars.userId, color: vars.color } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trip-members", tripId] });
      qc.invalidateQueries({ queryKey: ["admin-route"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update color"),
  });

  const [count, setCount] = useState(1);
  const [role, setRole] = useState<"member" | "admin">("member");
  const [note, setNote] = useState("");
  const [pickerMember, setPickerMember] = useState<any | null>(null);

  const createMut = useMutation({
    mutationFn: () => createFn({ data: { tripId: tripId!, count, role, note: note || undefined } }),
    onSuccess: (res) => {
      toast.success(`Created ${res.codes.length} code${res.codes.length === 1 ? "" : "s"}`);
      setNote("");
      qc.invalidateQueries({ queryKey: ["invites", tripId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to create codes"),
  });

  const revokeMut = useMutation({
    mutationFn: (code: string) => revokeFn({ data: { code } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", tripId] }),
    onError: (e: any) => toast.error(e.message ?? "Failed to revoke"),
  });

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error("Copy failed");
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-5 md:px-8 py-6 md:py-8 grid gap-6 overflow-x-hidden">
      <PageHeader
        title="Invites"
        subtitle="Each code lets one new person sign up and auto-join this trip. Share manually."
      />
      <RoavaCard
        title="Generate invite codes"
        description="Each code lets one person sign up and auto-join this trip."
      >
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_2fr_auto] items-end">
          <div className="grid gap-1.5">
            <Label htmlFor="count">How many</Label>
            <Input id="count" type="number" min={1} max={20} value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="h-11 rounded-xl" />
          </div>
          <div className="grid gap-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as any)}>
              <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="note">Note (optional)</Label>
            <Input id="note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. "For Sarah" or "Apple reviewer"' className="h-11 rounded-xl" maxLength={200} />
          </div>
          <PrimaryButton onClick={() => createMut.mutate()} disabled={!tripId || createMut.isPending}
            className="h-11 rounded-xl">
            {createMut.isPending ? "Generating…" : "Generate"}
          </PrimaryButton>
        </div>
      </RoavaCard>

      <section>
        <h2 className="text-lg font-semibold mb-1">Trip readiness</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Advisory snapshot across travelers. Tap a traveler for full device status.
        </p>
        <RoavaCard flush className="p-3">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
            <SummaryTile label="Ready" value={readinessSummary.ready} variant="success" />
            <SummaryTile label="Needs attention" value={readinessSummary.needsAttention} variant="warning" />
            <SummaryTile label="No status" value={readinessSummary.noStatus} variant="neutral" />
            <SummaryTile label="Tracking now" value={opsTotals.recentTracking} variant="info" />
            <SummaryTile label="Native queue" value={opsTotals.queued} variant="neutral" />
            <SummaryTile label="Failed media" value={opsTotals.failed} variant={opsTotals.failed > 0 ? "danger" : "neutral"} />
          </div>
        </RoavaCard>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-1">Traveler colors</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Each traveler's color is used for their map pins and route line.
        </p>
        <div className="grid gap-2">
          {(membersData?.members ?? []).map((m) => {
            const r = readinessByUser.get(m.userId);
            const variant = r ? readinessChipVariant(r.severity) : "neutral";
            const topReason = r && r.severity !== "ready" ? r.reasons[0]?.label : null;
            return (
              <Link
                key={m.userId}
                to="/admin/travelers/$userId"
                params={{ userId: m.userId }}
                className="block rounded-2xl transition hover:translate-x-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--roava-blue)]/40"
                aria-label={`Open ${m.displayName} status`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <TravelerColorChip
                      member={m}
                      onOpen={() => setPickerMember(m)}
                    />
                    {(r || topReason) && (
                      <div className="mt-1 flex items-center gap-2 pl-1 flex-wrap">
                        {r && (
                          <StatusChip variant={variant}>{r.status}</StatusChip>
                        )}
                        {topReason && (
                          <span className="text-[11px] text-[var(--roava-text-muted)] truncate max-w-[60vw]">
                            {topReason}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="size-5 text-[var(--roava-text-muted)] shrink-0" aria-hidden />
                </div>
              </Link>
            );
          })}
          {(membersData?.members ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">No travelers have joined yet.</p>
          )}
        </div>


        <TravelerColorPicker
          member={pickerMember}
          open={!!pickerMember}
          onClose={() => setPickerMember(null)}
          onSave={(color) => pickerMember && colorMut.mutate({ userId: pickerMember.userId, color })}
          onReset={() => pickerMember && colorMut.mutate({ userId: pickerMember.userId, color: null })}
          conflictNameByColor={Object.fromEntries(
            (membersData?.members ?? [])
              .filter((mm) => mm.userId !== pickerMember?.userId)
              .map((mm) => [(mm.color ?? colorForUser(mm.userId)).toLowerCase(), mm.displayName])
          )}
        />
      </section>


      <section>
        <h2 className="text-lg font-semibold mb-3 text-[var(--roava-text)]">All codes</h2>
        {isLoading && <p className="text-sm text-[var(--roava-text-muted)]">Loading…</p>}
        {!isLoading && (data?.codes ?? []).length === 0 && (
          <p className="text-sm text-[var(--roava-text-muted)]">No codes yet. Generate one above.</p>
        )}
        <div className="grid gap-2">
          {(data?.codes ?? []).map((c) => {
            const status = c.revoked_at ? "revoked" : c.redeemed_by ? "redeemed" : "available";
            const statusVariant =
              status === "available" ? "success" : status === "redeemed" ? "info" : "danger";
            return (
              <RoavaCard key={c.code} flush className="p-3 flex items-center gap-3 flex-wrap">
                <code className="font-mono text-sm font-semibold tracking-wider text-[var(--roava-text)]">{c.code}</code>
                <StatusChip variant="neutral">{c.role}</StatusChip>
                <StatusChip variant={statusVariant}>{status}</StatusChip>
                {c.note && <span className="text-xs text-[var(--roava-text-muted)]">{c.note}</span>}
                {c.redeemed_by_name && (
                  <span className="text-xs text-[var(--roava-text-muted)]">
                    → {c.redeemed_by_name}{c.redeemed_at ? ` on ${new Date(c.redeemed_at).toLocaleDateString()}` : ""}
                  </span>
                )}
                <div className="ml-auto flex gap-2">
                  {status === "available" && (
                    <>
                      <SecondaryButton size="sm" onClick={() => copy(c.code)}>Copy</SecondaryButton>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" disabled={revokeMut.isPending}>Revoke</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke invite code?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Code <span className="font-mono font-semibold">{c.code}</span> will stop working immediately. This can't be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction asChild>
                              <DangerButton onClick={() => revokeMut.mutate(c.code)}>
                                Revoke code
                              </DangerButton>
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </RoavaCard>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function SummaryTile({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "success" | "warning" | "danger" | "info" | "neutral";
}) {
  return (
    <div className="rounded-xl border border-[var(--roava-border)] bg-[var(--roava-card)]/40 py-2 px-1">
      <div className="flex justify-center mb-1">
        <StatusChip variant={variant}>{value}</StatusChip>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--roava-text-muted)] leading-tight">
        {label}
      </div>
    </div>
  );
}
